import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Switch,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RestoreIcon from '@mui/icons-material/Restore';

interface AdminEditDialogShellProps {
  open: boolean;
  onClose: () => void;
  accentColor: string;
  typeInitial: string;
  title: string;
  subtitle: string;
  formId?: string;
  onSave?: () => void;
  saveLabel?: string;
  isSaving?: boolean;
  isDirty?: boolean;
  isActive?: boolean;
  showActiveToggle?: boolean;
  onToggleActive?: () => void;
  onReset?: () => void;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
}

const AdminEditDialogShell: React.FC<AdminEditDialogShellProps> = ({
  open,
  onClose,
  accentColor,
  typeInitial,
  title,
  subtitle,
  formId,
  onSave,
  saveLabel = 'Save changes',
  isSaving = false,
  isDirty = false,
  isActive,
  showActiveToggle = false,
  onToggleActive,
  onReset,
  maxWidth = 'md',
  children,
}) => {
  const saveButtonProps: any = {};
  if (formId) {
    saveButtonProps.form = formId;
    saveButtonProps.type = 'submit';
  } else if (onSave) {
    saveButtonProps.onClick = onSave;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      PaperProps={{
        sx: {
          borderLeft: `4px solid ${accentColor}`,
          borderRadius: '12px',
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          {/* Icon square */}
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '8px',
              backgroundColor: accentColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: '#fff',
                fontWeight: 700,
                fontSize: '1.2rem',
                lineHeight: 1,
              }}
            >
              {typeInitial}
            </Typography>
          </Box>
          {/* Title + subtitle */}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '1.1rem',
                lineHeight: 1.3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}
            >
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {showActiveToggle && (
            <>
              <Chip
                label={isActive ? 'Active' : 'Inactive'}
                size="small"
                sx={{
                  backgroundColor: isActive ? `${accentColor}20` : 'action.hover',
                  color: isActive ? accentColor : 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
              <Switch
                checked={!!isActive}
                onChange={onToggleActive}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: accentColor,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: accentColor,
                  },
                }}
              />
            </>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 0 }}>
        {children}
      </DialogContent>

      {/* Amber dirty bar */}
      {isDirty && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            py: 1,
            backgroundColor: '#FFF8E1',
            borderTop: '1px solid #FFE082',
          }}
        >
          <WarningAmberIcon sx={{ color: '#F9A825', fontSize: '1.1rem' }} />
          <Typography sx={{ color: '#E65100', fontSize: '0.8rem', fontWeight: 500 }}>
            Unsaved changes — click {saveLabel} to write to database
          </Typography>
        </Box>
      )}

      {/* Footer */}
      <DialogActions
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'grey.50',
          px: 3,
          py: 1.5,
          justifyContent: 'space-between',
        }}
      >
        <Box>
          {onReset && (
            <Button
              startIcon={<RestoreIcon />}
              onClick={onReset}
              size="small"
              sx={{ color: 'text.secondary', textTransform: 'none' }}
            >
              Reset to defaults
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              backgroundColor: accentColor,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: accentColor,
                filter: 'brightness(0.9)',
              },
            }}
            {...saveButtonProps}
          >
            {isSaving ? 'Saving...' : saveLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AdminEditDialogShell;
